import * as React from "react";
import { AutoQuoteSettings, Branch } from "gen/clients";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { Form, Formik } from "formik";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import * as Yup from "yup";
import { getCurrencyName } from "utils/currencyUtils";

interface Props {
  open: boolean;
  branch: Branch;
  currentSettings: AutoQuoteSettings;
  onClose: () => void;
  onSave: (branchId: string, settings: AutoQuoteSettings) => Promise<void>;
}

enum FieldNames {
  pricePerPage = "pricePerPage",
  pageCountEscalationThreshold = "pageCountEscalationThreshold",
  quoteExpiration = "quoteExpiration"
}

interface FormValues {
  [FieldNames.pricePerPage]: string;
  [FieldNames.pageCountEscalationThreshold]: string;
  [FieldNames.quoteExpiration]: string;
}

const validationSchema = Yup.object().shape({
  [FieldNames.pricePerPage]: Yup.string()
    .required("Price per page is required")
    .test("is-positive-number", "Price must be a positive number", value => {
      if (!value) {
        return false;
      }
      const num = parseFloat(value);
      return !isNaN(num) && num > 0;
    }),
  [FieldNames.pageCountEscalationThreshold]: Yup.string()
    .required("Page count escalation threshold is required")
    .test("is-positive-integer", "Must be a positive integer", value => {
      if (!value) {
        return false;
      }
      const num = parseInt(value, 10);
      return !isNaN(num) && num >= 0 && Number.isInteger(num);
    }),
  [FieldNames.quoteExpiration]: Yup.string()
    .required("Quote expiration is required")
    .test("is-positive-integer", "Must be a positive integer", value => {
      if (!value) {
        return false;
      }
      const num = parseInt(value, 10);
      return !isNaN(num) && num > 0 && Number.isInteger(num);
    })
});

const EditBranchSettingsDialog: React.FC<Props> = ({ open, branch, currentSettings, onClose, onSave }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const initialValues: FormValues = {
    [FieldNames.pricePerPage]: currentSettings.pricePerPage.toString(),
    [FieldNames.pageCountEscalationThreshold]: currentSettings.pageCountEscalationThreshold.toString(),
    [FieldNames.quoteExpiration]: currentSettings.quoteExpiration.toString()
  };

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      const settings: AutoQuoteSettings = {
        pricePerPage: parseFloat(values[FieldNames.pricePerPage]),
        pageCountEscalationThreshold: parseInt(values[FieldNames.pageCountEscalationThreshold], 10),
        quoteExpiration: parseInt(values[FieldNames.quoteExpiration], 10),
        rejectedReasonsAnalyticsAIPrompt: currentSettings.rejectedReasonsAnalyticsAIPrompt
      };
      await onSave(branch.id, settings);
      onClose(); // Only close on success
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred while saving the settings");
    } finally {
      setIsLoading(false);
    }
  };

  // Reset error when dialog opens
  React.useEffect(() => {
    if (open) {
      setError(null);
      setIsLoading(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isValid, dirty }) => (
          <Form>
            <DialogTitle>Edit Auto-Quote Settings - {branch.name}</DialogTitle>

            <DialogContent>
              <Stack spacing={3} sx={{ mt: 1 }}>
                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}

                <TextInputField
                  name={FieldNames.pricePerPage}
                  label="Price Per Page"
                  type="number"
                  required
                  disabled={isLoading}
                  helperText={`Enter the price per page in ${getCurrencyName(branch.currency)}`}
                  inputProps={{
                    min: "0.01",
                    step: "0.01"
                  }}
                />

                <TextInputField
                  name={FieldNames.pageCountEscalationThreshold}
                  label="Page Count Escalation Threshold"
                  type="number"
                  required
                  disabled={isLoading}
                  helperText="Number of pages at which a human review is required"
                  inputProps={{
                    min: "0",
                    step: "1"
                  }}
                />

                <TextInputField
                  name={FieldNames.quoteExpiration}
                  label="Quote Expiration Period (days)"
                  type="number"
                  required
                  disabled={isLoading}
                  helperText="Number of days until the quote expires"
                  inputProps={{
                    min: "1",
                    step: "1"
                  }}
                />
              </Stack>
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!isValid || !dirty || isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
              >
                {isLoading ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { EditBranchSettingsDialog };
